import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Line, LineChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { DadosIrradiacao } from '@/services/irradiacaoService';
import { Sun } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface IrradiacaoChartProps {
  dados: DadosIrradiacao | null;
  loading?: boolean;
}

export function IrradiacaoChart({ dados, loading = false }: IrradiacaoChartProps) {
  const isMobile = useIsMobile();

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
            Irradiação Local
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Carregando dados de irradiação...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`${isMobile ? 'h-64' : 'h-80'} flex items-center justify-center`}>
            <div className="text-muted-foreground text-sm">Carregando...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dados) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
            Irradiação Local
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Dados de irradiação não encontrados para esta localização
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`${isMobile ? 'h-64' : 'h-80'} flex items-center justify-center`}>
            <div className="text-muted-foreground text-sm px-4 text-center">
              Não foi possível carregar os dados de irradiação para {dados?.cidade || 'esta localização'}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Formatar dados para o gráfico
  const dadosGrafico = [
    { mes: 'Jan', irradiacao: dados.janeiro },
    { mes: 'Fev', irradiacao: dados.fevereiro },
    { mes: 'Mar', irradiacao: dados.marco },
    { mes: 'Abr', irradiacao: dados.abril },
    { mes: 'Mai', irradiacao: dados.maio },
    { mes: 'Jun', irradiacao: dados.junho },
    { mes: 'Jul', irradiacao: dados.julho },
    { mes: 'Ago', irradiacao: dados.agosto },
    { mes: 'Set', irradiacao: dados.setembro },
    { mes: 'Out', irradiacao: dados.outubro },
    { mes: 'Nov', irradiacao: dados.novembro },
    { mes: 'Dez', irradiacao: dados.dezembro }
  ];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
          Irradiação Local (kWh/m²/dia)
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          {dados.cidade}, {dados.estado} - Variação mensal da irradiação solar
        </CardDescription>
      </CardHeader>
      <CardContent className={`w-full ${isMobile ? 'px-2 sm:px-4' : 'px-8'}`}>
        <div className={`w-full ${isMobile ? 'h-64' : 'h-80'}`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              data={dadosGrafico}
              margin={{ 
                top: isMobile ? 10 : 20, 
                right: isMobile ? 10 : 30, 
                left: isMobile ? 5 : 20, 
                bottom: isMobile ? 20 : 5 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="mes" 
                stroke="#64748b"
                fontSize={isMobile ? 10 : 12}
                tickLine={false}
                axisLine={false}
                tickMargin={isMobile ? 5 : 10}
                interval={0} // Mostra todos os meses
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? 'end' : 'middle'}
                height={isMobile ? 60 : 30}
              />
              <YAxis 
                stroke="#64748b"
                fontSize={isMobile ? 10 : 12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value.toFixed(1)}`}
                domain={[0, 'dataMax + 1']}
                tickMargin={isMobile ? 5 : 10}
                width={isMobile ? 40 : 60}
              />
              <Tooltip 
                formatter={(value: number) => [`${value.toFixed(2)} kWh/m²/dia`, 'Irradiação']}
                labelFormatter={(label) => `Mês: ${label}`}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '8px' : '12px'
                }}
              />
              <Line
                type="monotone"
                dataKey="irradiacao"
                stroke="#2563eb"
                strokeWidth={isMobile ? 2 : 3}
                dot={{ fill: '#2563eb', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                activeDot={{ r: isMobile ? 5 : 6, stroke: '#2563eb', strokeWidth: 3 }}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Informações adicionais */}
        <div className={`mt-4 grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className={`font-medium text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>Média Anual</p>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-amber-600`}>
              {dados.media.toFixed(2)} kWh/m²/dia
            </p>
          </div>
          <div className="text-center p-3 bg-muted/30 rounded-lg">
            <p className={`font-medium text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>Melhor Mês</p>
            <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-blue-600`}>
              {Math.max(...dadosGrafico.map(d => d.irradiacao)).toFixed(2)} kWh/m²/dia
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
